import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

// 공휴일 관리: 모든 교회 앱의 '교회일정' 목록에 함께 표시되는 플랫폼 공통 공휴일(siteAdmin.holidays).
type Holiday = { id: number; date: string; name: string };

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string; added?: number };

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayOf(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const EMPTY_FORM = { date: '', name: '' };

export default function HolidayManagement() {
  const session = getAdminSession();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [items, setItems] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Holiday[]>>(`${MainURL}/admin-console/holidays`, { params: { year } });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '공휴일 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '공휴일 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const startEdit = (holiday: Holiday) => {
    setEditingId(holiday.id);
    setForm({ date: holiday.date, name: holiday.name });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.date || !form.name.trim()) {
      alert('날짜와 공휴일 이름을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const payload = { requesterId: session.id, date: form.date, name: form.name.trim() };
      const res = editingId
        ? await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/holidays/${editingId}`, payload)
        : await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/holidays`, payload);
      if (!res.data?.ok) throw new Error(res.data?.message);
      cancelEdit();
      const savedYear = Number(form.date.slice(0, 4));
      if (savedYear !== year) setYear(savedYear);
      else fetchHolidays();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (holiday: Holiday) => {
    if (!session) return;
    if (!window.confirm(`${holiday.date} '${holiday.name}'을(를) 삭제하시겠습니까?\n모든 교회 앱의 교회일정에서 사라집니다.`)) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/holidays/${holiday.id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== holiday.id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  const importFixed = async () => {
    if (!session) return;
    if (!window.confirm(`${year}년 양력 고정 공휴일(신정·삼일절·어린이날·현충일·광복절·개천절·한글날·성탄절)을 추가할까요?\n이미 등록된 공휴일은 건너뜁니다.`)) return;
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/holidays/fixed`, {
        requesterId: session.id,
        year,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      alert(`${res.data.added ?? 0}건을 추가했습니다.`);
      fetchHolidays();
    } catch (err) {
      alert(errorMessageOf(err, '고정 공휴일을 추가하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">공휴일 관리</h2>
          <p className="admin-panel-desc">
            여기서 등록한 공휴일은 모든 교회 앱(성도용·관리자앱, 교회앱관리 웹)의 '교회일정' 목록에 함께 표시됩니다.
            설날·추석·부처님오신날과 대체공휴일은 해마다 날짜가 달라 직접 등록해 주세요.
          </p>
        </div>
        <div className="admin-form-actions">
          <button type="button" className="admin-cancel-btn" onClick={() => setYear(year - 1)}>
            ‹ {year - 1}
          </button>
          <strong style={{ fontSize: 18, padding: '0 8px' }}>{year}년</strong>
          <button type="button" className="admin-cancel-btn" onClick={() => setYear(year + 1)}>
            {year + 1} ›
          </button>
          <button type="button" className="admin-refresh-btn" onClick={importFixed}>
            양력 고정 공휴일 추가
          </button>
        </div>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
          style={{ maxWidth: 180 }}
        />
        <input
          type="text"
          placeholder="공휴일 이름 (예: 추석, 대체공휴일)"
          value={form.name}
          maxLength={100}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
        />
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            {editingId ? '수정 저장' : '공휴일 등록'}
          </button>
          {editingId && (
            <button type="button" className="admin-cancel-btn" onClick={cancelEdit}>
              취소
            </button>
          )}
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>날짜</th>
              <th>요일</th>
              <th>공휴일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4}>불러오는 중...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4}>{year}년에 등록된 공휴일이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.date}</td>
                  <td>{weekdayOf(item.date)}</td>
                  <td style={{ textAlign: 'left' }}>{item.name}</td>
                  <td>
                    <button type="button" className="admin-btn edit-btn" onClick={() => startEdit(item)}>
                      수정
                    </button>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
